import { inject, Injectable } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { of, tap } from 'rxjs';
import { getBrowserTimeZone } from '@lx/quote-tool/shared/utils/functions';
import { CountryRegionType, CountryRegionTypeEnum, ResidenceCountry, ResidenceCountryService } from '@lx/shared/data-access/destination';
import { SharedDataStore } from '../store';
import { TIMEZONES } from '@lx/quote-tool/shared/types';

@Injectable({
  providedIn: 'root',
})
export class ResidenceDataService {
  private readonly residenceCountryService = inject(ResidenceCountryService);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly timeZones = inject(TIMEZONES);

  /** Fetch residence countries (from cache or API) */
  loadResidenceCountries() {
    return this.sharedDataStore.residenceCountries$.pipe(
      switchMap((countries: ResidenceCountry[]) =>
        countries?.length
          ? of(countries) // Return cached data if available
          : this.residenceCountryService.getAll().pipe(
              tap((residenceCountries) => {
                // Update store with the fetched countries if data is from the API
                this.sharedDataStore.updateState({ residenceCountries });
              }),
            ),
      ),
    );
  }

  detectCountryCode(): string | undefined {
    // Get the user's timezone
    const userTimezone = getBrowserTimeZone();

    // Find the country code based on the user's timezone
    const [countryCode] = Object.entries(this.timeZones).find(([, timezones]) => timezones.includes(userTimezone)) ?? [];

    return countryCode;
  }

  getCountryRegionLabel(countryRegionType: CountryRegionType): string {
    if (!countryRegionType) {
      return null;
    }
    switch (countryRegionType) {
      case CountryRegionTypeEnum.CITY:
        return $localize`City`;
      case CountryRegionTypeEnum.STATE:
        return $localize`State`;
      case CountryRegionTypeEnum.POSTALCODE:
        return $localize`Postal code`;
    }
  }
}
